import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { authKeys } from '~/api/auth';
import { api, inlineError, isApiError, unwrap } from '~/api/client';
import { AuthCard } from '~/app/AuthCard';
import { Button } from '~/ui/Button';
import { PasswordInput } from '~/ui/PasswordInput';
import { Notice } from '~/ui/Notice';
import { Skeleton } from '~/ui/Skeleton';

export const Route = createFileRoute('/invite/$token')({
  component: InvitePage,
});

const MIN = 10;

function InvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const lookup = useQuery({
    queryKey: ['invite', token],
    queryFn: () => unwrap(api.GET('/api/v1/auth/invite/{token}', { params: { path: { token } } })),
    retry: false,
  });

  // When the email already has an account, its own password joins it: a link alone
  // never opens an existing account. Known only after the lookup resolves.
  const hasAccount = lookup.data?.hasAccount ?? false;

  const accept = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/auth/invite/{token}', {
          params: { path: { token } },
          body: { password, kind: 'web' },
        }),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: authKeys.me });
      await navigate({ to: '/my-day' });
    },
  });

  const longEnough = password.length >= MIN;
  const matches = confirm.length > 0 && confirm === password;
  const canSubmit =
    (hasAccount ? password.length > 0 : longEnough && matches) && !accept.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (canSubmit) accept.mutate();
  };

  if (lookup.isPending) {
    return (
      <AuthCard title="Checking your invite…">
        <Skeleton lines={3} />
      </AuthCard>
    );
  }

  if (lookup.isError) {
    const err = lookup.error;
    const text = isApiError(err)
      ? err.isOffline
        ? err.message
        : err.status === 404
          ? 'This invite link is no longer valid. Ask your manager for a new one.'
          : err.message
      : 'Something went wrong. Try again.';
    return (
      <AuthCard title="This link cannot be used.">
        <Notice tone="late">{text}</Notice>
      </AuthCard>
    );
  }

  const acceptMessage = inlineError(accept.error);

  return (
    <AuthCard
      title={`Welcome, ${lookup.data.name}.`}
      subtitle={hasAccount ? 'Join this company with your account.' : 'Set a password to start.'}
    >
      <p className="mb-4 text-sm text-ink-2">
        You are joining as <span className="font-mono">{lookup.data.email}</span>.
      </p>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        {hasAccount ? (
          <>
            <Notice>
              This email already has an 11Deeds account. Enter its password to join. Forgot it? Ask
              whoever sent this link; if you use 11Deeds only with them, they can reset it.
            </Notice>
            <PasswordInput
              label="Your 11Deeds password"
              name="current-password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </>
        ) : (
          <>
            <PasswordInput
              label="Password"
              name="new-password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <PasswordInput
              label="Type it again"
              name="confirm-password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
            <ul className="flex flex-col gap-1 text-sm" aria-live="polite">
              <Rule met={longEnough}>At least {MIN} characters</Rule>
              <Rule met={matches}>Both fields match</Rule>
            </ul>
          </>
        )}
        {acceptMessage && <Notice tone="late">{acceptMessage}</Notice>}
        <Button type="submit" block disabled={!canSubmit} loading={accept.isPending}>
          {hasAccount ? 'Join and start' : 'Set password and start'}
        </Button>
      </form>
    </AuthCard>
  );
}

function Rule({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <li className={`flex items-center gap-2 ${met ? 'text-done' : 'text-ink-3'}`}>
      <Check size={16} aria-hidden className={met ? '' : 'opacity-30'} />
      {children}
    </li>
  );
}
